// Google Maps address picker — modal body for InputLocation

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Input from './Input';
import Spinner from './Spinner';
import { useModal } from './ModalProvider';
import { debounce } from '../utils/misc';
import './LocationPicker.scss';

// Pinned to American English: left to itself the bootstrap follows the browser
// locale, and a geocode would come back localized — the «, City, ST» tail that
// cityOf() reads back out of a saved value only exists in the US form
const MAPS_SRC =
  'https://maps.googleapis.com/maps/api/js?v=weekly&loading=async&language=en&region=US';

// The organization's home city — where the map opens with nothing to show
const DEFAULT_CENTER = { lat: 37.3382, lng: -121.8863 };
const DEFAULT_ZOOM = 11;

// A team's location is where it is from, and both lists bucket their Location
// filter by exactly this string — a street address would shatter it
const CITY_TYPES = ['locality', 'administrative_area_level_3'];

let mapsPromise = null;

// One <script> per session, injected on first open rather than sitting in
// index.html — otherwise every public page pays for an API nobody opened
const loadMaps = () => {
  if (mapsPromise) return mapsPromise;

  mapsPromise = new Promise((resolve, reject) => {
    // `loading=async` warns unless the bootstrap gets a callback to hand back
    window.__muvaMapsReady = () => {
      delete window.__muvaMapsReady;
      resolve(window.google.maps);
    };
    // A rejected key loads the script fine and only fails at runtime, so
    // without this the picker would show a dead map and say nothing
    window.gm_authFailure = () => toast.error('Google Maps rejected the key');
    const script = document.createElement('script');
    script.src = `${MAPS_SRC}&callback=__muvaMapsReady&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
    script.async = true;
    script.onerror = () => {
      // Retry from a clean slate on the next open, rather than appending a
      // second copy of a script Google refuses to see twice
      delete window.__muvaMapsReady;
      script.remove();
      mapsPromise = null;
      reject(new Error('Could not load Google Maps'));
    };
    document.head.appendChild(script);
  });

  return mapsPromise;
};

// Places API (New) returns longText/shortText, the classic Geocoder
// long_name/short_name. One shape downstream.
const normalizeComponents = (components) =>
  (components ?? []).map((component) => ({
    long: component.longText ?? component.long_name ?? '',
    short: component.shortText ?? component.short_name ?? '',
    types: component.types ?? [],
  }));

const pick = (components, type, key = 'long') =>
  components.find((component) => component.types.includes(type))?.[key] ?? '';

const cityPart = (components) =>
  pick(components, 'locality') ||
  pick(components, 'postal_town') ||
  pick(components, 'sublocality') ||
  pick(components, 'administrative_area_level_3') ||
  // Last resort — the county. A point off any town still has to put something
  // in the city slot, or the tail stops being a filter key
  pick(components, 'administrative_area_level_2');

// «Riverside Complex, 1234 Main St, Austin, TX 78701» or «Austin, TX». The tail
// is fixed so cityOf() can read the filter key back out of a saved value.
const formatAddress = (components, { level, displayName } = {}) => {
  const city = cityPart(components);
  const state =
    pick(components, 'administrative_area_level_1', 'short') ||
    pick(components, 'country', 'short');

  if (level === 'city') return [city, state].filter(Boolean).join(', ');

  const street = [pick(components, 'street_number'), pick(components, 'route')]
    .filter(Boolean)
    .join(' ');

  return [
    displayName,
    street,
    city,
    [state, pick(components, 'postal_code')].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
};

// Only a venue earns a name in front of the street — for a plain address Google
// returns the street itself as displayName, which would just repeat
const isVenue = (types) =>
  (types ?? []).some(
    (type) => type === 'establishment' || type === 'point_of_interest',
  );

export default function LocationPicker({ initial, level = 'address', onPick }) {
  const { closeModal } = useModal();
  const [spinner, setSpinner] = useState(true);
  const [ready, setReady] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [picked, setPicked] = useState(null);

  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const placesRef = useRef(null);
  const geocoderRef = useRef(null);
  const tokenRef = useRef(null);
  const listenersRef = useRef([]);
  // Two pins dropped in a row must not let the slower geocode win
  const geocodeSeq = useRef(0);

  const showAt = useCallback(
    (position) => {
      if (markerRef.current) markerRef.current.position = position;
      const map = mapRef.current;
      if (map) {
        map.panTo(position);
        // Only ever closer: a pick made while zoomed in must not pull back out
        const zoom = level === 'city' ? 11 : 17;
        if (map.getZoom() < zoom) map.setZoom(zoom);
      }
    },
    [level],
  );

  const applyPlace = useCallback(
    ({ components, displayName, position }) => {
      setPicked({ components, displayName });
      setSuggestions([]);
      showAt(position);
    },
    [showAt],
  );

  const resolveLatLng = useCallback(
    async (latLng) => {
      // A map click hands over a LatLng, a dragged marker its own position,
      // which is a plain literal
      const point =
        typeof latLng?.lat === 'function' ? latLng.toJSON() : latLng;
      if (!geocoderRef.current || !point) return;
      const seq = ++geocodeSeq.current;
      setSpinner(true);
      try {
        const { results } = await geocoderRef.current.geocode({
          location: point,
        });
        if (seq !== geocodeSeq.current) return;
        if (!results?.length) {
          toast.error('No address at this point');
          return;
        }
        const components = normalizeComponents(results[0].address_components);
        // Off any town the city slot stays empty, and the value would format
        // to nothing — Select would sit disabled without saying why
        if (level === 'city' && !cityPart(components)) {
          toast.error('No city at this point');
          return;
        }
        applyPlace({ components, position: point });
      } catch (err) {
        if (seq === geocodeSeq.current)
          toast.error(err?.message ?? 'Could not resolve this point');
      } finally {
        if (seq === geocodeSeq.current) setSpinner(false);
      }
    },
    [applyPlace, level],
  );

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (mapRef.current) return;
      try {
        const maps = await loadMaps();
        const [{ Map }, { AdvancedMarkerElement }, places, { Geocoder }] =
          await Promise.all([
            maps.importLibrary('maps'),
            maps.importLibrary('marker'),
            maps.importLibrary('places'),
            maps.importLibrary('geocoding'),
          ]);
        if (cancelled || !mapNodeRef.current) return;

        placesRef.current = places;
        geocoderRef.current = new Geocoder();
        tokenRef.current = new places.AutocompleteSessionToken();

        const map = new Map(mapNodeRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'greedy',
        });
        const marker = new AdvancedMarkerElement({ map, gmpDraggable: true });
        mapRef.current = map;
        markerRef.current = marker;
        setReady(true);

        // The pin resolves to whatever `level` asks for, a city included
        listenersRef.current.push(
          map.addListener('click', (event) => resolveLatLng(event.latLng)),
          // AdvancedMarkerElement drag events do not carry latLng reliably
          marker.addListener('dragend', () =>
            resolveLatLng(markerRef.current?.position),
          ),
        );

        if (initial) {
          try {
            const { results } = await geocoderRef.current.geocode({
              address: initial,
            });
            const best = results?.[0];
            // Shown, never pre-picked: geocoding the saved string back loses a
            // venue name, so Select must stay a deliberate act
            if (best && !cancelled) showAt(best.geometry.location.toJSON());
          } catch (err) {
            // A legacy value that geocodes to nothing is normal, not an error
          }
        }
      } catch (err) {
        toast.error(err?.message ?? 'Could not load Google Maps');
      } finally {
        if (!cancelled) setSpinner(false);
      }
    };

    init();

    // The modal only hides its body — without this the map, its listeners and
    // its tile requests would outlive every close
    return () => {
      cancelled = true;
      listenersRef.current.forEach((listener) => listener.remove());
      listenersRef.current = [];
      if (markerRef.current) markerRef.current.map = null;
      markerRef.current = null;
      mapRef.current = null;
    };
  }, [initial, showAt, resolveLatLng]);

  const fetchSuggestions = useCallback(
    async (input) => {
      const places = placesRef.current;
      if (!places || !input.trim()) {
        setSuggestions([]);
        return;
      }
      try {
        const { suggestions: found } =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            sessionToken: tokenRef.current,
            ...(level === 'city' ? { includedPrimaryTypes: CITY_TYPES } : {}),
          });
        setSuggestions((found ?? []).filter((item) => item.placePrediction));
      } catch (err) {
        toast.error(err?.message ?? 'Search failed');
      }
    },
    [level],
  );

  const search = useMemo(
    () => debounce(fetchSuggestions, 300),
    [fetchSuggestions],
  );

  useEffect(() => () => search.cancel(), [search]);

  const choose = async (suggestion) => {
    // The keystroke that is still on the clock, and any pin geocode in flight,
    // would both land on top of this pick
    search.cancel();
    geocodeSeq.current += 1;
    setSpinner(true);
    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({
        fields: ['displayName', 'addressComponents', 'location', 'types'],
      });
      // The details call closes the session; the next search opens a new one
      tokenRef.current = new placesRef.current.AutocompleteSessionToken();
      applyPlace({
        components: normalizeComponents(place.addressComponents),
        displayName: isVenue(place.types) ? place.displayName : '',
        position: place.location.toJSON(),
      });
      setQuery('');
    } catch (err) {
      toast.error(err?.message ?? 'Could not read this place');
    } finally {
      setSpinner(false);
    }
  };

  const value = picked
    ? formatAddress(picked.components, {
        level,
        displayName: picked.displayName,
      })
    : '';

  return (
    <div className='location-picker'>
      <div className='search-wrap'>
        <Input
          name='location-search'
          placeholder={level === 'city' ? 'Search city' : 'Search address'}
          value={query}
          // Until Places answers, a keystroke would be dropped without a word
          disabled={!ready}
          onChange={(e) => {
            setQuery(e.target.value);
            search(e.target.value);
          }}
          onClear={
            query
              ? () => {
                  search.cancel();
                  setQuery('');
                  setSuggestions([]);
                }
              : undefined
          }
        />
        {suggestions.length > 0 && (
          <ul className='suggestions'>
            {suggestions.map((suggestion) => (
              <li
                key={suggestion.placePrediction.placeId}
                onClick={() => choose(suggestion)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  // Space would scroll the list out from under the caret
                  e.preventDefault();
                  choose(suggestion);
                }}
              >
                {suggestion.placePrediction.text.text}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className='map' ref={mapNodeRef} />
      <p className='picked'>
        {value ||
          (level === 'city'
            ? 'Search, or tap the map to pick a city'
            : 'Search, or tap the map to drop a pin')}
      </p>
      <Spinner show={spinner} />
      <div className='actions'>
        <button type='button' className='bw' onClick={closeModal}>
          Cancel
        </button>
        <button
          type='button'
          disabled={!value || spinner}
          onClick={() => {
            onPick(value);
            closeModal();
          }}
        >
          Select
        </button>
      </div>
    </div>
  );
}
